import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { createContext } from "react";
import { DEFAULT_ITEM_CATALOG_SUB_ENV, getStoredItemCatSubEnv, ItemCatSubEnv, storeItemCatSubEnv } from "@/utilities/itemcat/itemCatSubEnv";

export const AppContext = createContext<{
  autoUpdate: boolean;
  setAutoUpdate: Dispatch<SetStateAction<boolean>>;
  uploadFile: any;
  setUploadFile: Dispatch<SetStateAction<any>>;
  uploadComplete: boolean;
  setUploadComplete: Dispatch<SetStateAction<any>>;
  cancelAxiosSource: any;
  setCancelAxiosSource: Dispatch<SetStateAction<any>>;
  uploadResetCounter: number;
  setUploadResetCounter: Dispatch<SetStateAction<number>>;
  firstLoadingJob: boolean;
  setFirstLoadingJob: Dispatch<SetStateAction<any>>;
  initPLC: boolean;
  setInitPLC: Dispatch<SetStateAction<any>>;
  admin: boolean;
  setAdmin: Dispatch<SetStateAction<any>>;
  isBtdUser: boolean;
  setIsBtdUser: Dispatch<SetStateAction<any>>;
  itemCatSubEnv: ItemCatSubEnv;
  setItemCatSubEnv: (value: ItemCatSubEnv) => void;
  itemCreateAllowed: boolean;
  setItemCreateAllowed: Dispatch<SetStateAction<boolean>>;
}>({
  autoUpdate: false,
  setAutoUpdate: () => new Promise((resolve) => resolve(null)),
  uploadFile: null,
  setUploadFile: () => new Promise((resolve) => resolve(null)),
  uploadComplete: false,
  setUploadComplete: () => new Promise((resolve) => resolve(null)),
  cancelAxiosSource: false,
  setCancelAxiosSource: () => new Promise((resolve) => resolve(null)),
  uploadResetCounter: 0,
  setUploadResetCounter: () => new Promise((resolve) => resolve(null)),
  firstLoadingJob: false,
  setFirstLoadingJob: () => new Promise((resolve) => resolve(null)),
  initPLC: false,
  setInitPLC: () => new Promise((resolve) => resolve(null)),
  admin: false,
  setAdmin: () => new Promise((resolve) => resolve(null)),
  isBtdUser: false,
  setIsBtdUser: () => new Promise((resolve) => resolve(null)),
  itemCatSubEnv: DEFAULT_ITEM_CATALOG_SUB_ENV,
  setItemCatSubEnv: () => new Promise((resolve) => resolve(null)),
  itemCreateAllowed: false,
  setItemCreateAllowed: () => new Promise((resolve) => resolve(null))
});

export function AppContextProvider({ children }: { children: React.ReactNode }) {
  const [autoUpdate, setAutoUpdate] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<any>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [cancelAxiosSource, setCancelAxiosSource] = useState(null);
  const [uploadResetCounter, setUploadResetCounter] = useState<number>(0);
  const [firstLoadingJob, setFirstLoadingJob] = useState(false);
  const [initPLC, setInitPLC] = useState<boolean>(false);
  const [admin, setAdmin] = useState<boolean>(false);
  const [isBtdUser, setIsBtdUser] = useState<boolean>(false);
  // Initialize to the SSR-safe default (never read localStorage during the lazy
  // initializer) — Next.js renders this component once with no `window` (build/SSR)
  // and once on the client for hydration; reading localStorage in the initializer
  // makes those two renders disagree, so the Select shows the wrong value until
  // some unrelated re-render happens to catch it up. Sync the real value after
  // mount instead, once we're guaranteed to be running client-side.
  const [itemCatSubEnv, setItemCatSubEnvState] = useState<ItemCatSubEnv>(DEFAULT_ITEM_CATALOG_SUB_ENV);
  // No BE "am I MDM/NPI" endpoint exists yet, so this is set from a regular Item fetch:
  // useItemList reads IsMdmOrNpiTeam off the first row of whatever it just fetched (Team
  // membership alone, independent of that row's own state — see ItemRecord's doc comment)
  // and is reset on env/sub-env switch, since Team membership is per-env (Viral, 2026-09-02).
  const [itemCreateAllowed, setItemCreateAllowed] = useState<boolean>(false);

  useEffect(() => {
    setItemCatSubEnvState(getStoredItemCatSubEnv());
  }, []);

  const setItemCatSubEnv = (value: ItemCatSubEnv) => {
    storeItemCatSubEnv(value);
    setItemCatSubEnvState(value);
  };

  return (
    <AppContext.Provider
      value={{
        autoUpdate,
        setAutoUpdate,
        uploadFile,
        setUploadFile,
        uploadComplete,
        setUploadComplete,
        cancelAxiosSource,
        setCancelAxiosSource,
        uploadResetCounter,
        setUploadResetCounter,
        firstLoadingJob,
        setFirstLoadingJob,
        initPLC,
        setInitPLC,
        admin,
        setAdmin,
        isBtdUser,
        setIsBtdUser,
        itemCatSubEnv,
        setItemCatSubEnv,
        itemCreateAllowed,
        setItemCreateAllowed
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
